
import React from "react";
import { Tooltip, OverlayTrigger } from 'react-bootstrap'
import { Trash, Edit, Download } from "react-feather";
import {
    convertDateToDisplay,
    convertMilliseconds,
    NumberRangeColumnFilter,
    SelectColumnFilter,
    returnDrawType,
    downloadQRCode, createQRCodePlatform01,
    getOriginLabel, parseFlexibleDate, getDateTimestamp,
    normalizeJsonObject, getJsonFieldValue,
} from "../assets/functions.js";

const safeParseObject = (value, fallback = {}) => {
    if (!value) {
        return fallback;
    }

    if (typeof value === 'object') {
        return value;
    }

    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' ? parsed : fallback;
    } catch (error) {
        return fallback;
    }
};

const isDateInRange = (value, dateRange = {}) => {
    const timestamp = getDateTimestamp(value);
    if (timestamp === null) {
        return false;
    }

    const hasStartDate = Boolean(dateRange.startDate);
    const hasEndDate = Boolean(dateRange.endDate);
    const startTimestamp = hasStartDate ? getDateTimestamp(dateRange.startDate) : null;
    const endTimestamp = hasEndDate ? getDateTimestamp(dateRange.endDate) : null;

    if ((hasStartDate && startTimestamp === null) || (hasEndDate && endTimestamp === null)) {
        return false;
    }

    return (!hasStartDate || timestamp >= startTimestamp)
        && (!hasEndDate || timestamp <= endTimestamp);
};

const getApp11MediaItems = (media, fallbackDate) => {
    if (!Array.isArray(media)) {
        return [];
    }

    return media
        .map((mediaItem) => {
            if (!mediaItem || typeof mediaItem !== 'object' || Array.isArray(mediaItem) || typeof mediaItem.url !== 'string') {
                return null;
            }

            const url = mediaItem.url.trim();

            if (!url) {
                return null;
            }

            return {
                ...mediaItem,
                url,
                data: mediaItem.data || fallbackDate,
            };
        })
        .filter(Boolean);
};

const expandApp11MediaRows = (rows, appData, filter) => {
    const expandedRows = [];

    rows.forEach((historyItem) => {
        const parsedItem = safeParseObject(historyItem.json);
        const mediaItems = getApp11MediaItems(parsedItem.media, historyItem.data);

        mediaItems.forEach((mediaItem, mediaIndex) => {
            const mediaDate = mediaItem.data || historyItem.data;

            expandedRows.push({
                ...historyItem,
                ...parsedItem,
                customizacao_id: appData.customizacao_id,
                evento_id: appData.evento_id,
                visitante_id: historyItem.visitante_id,
                media: mediaItem.url,
                mediaItem,
                mediaIndex,
                mediaKey: `${historyItem.visitante_id}-${mediaIndex}`,
                mediaPosition: String(mediaIndex + 1).padStart(2, '0'),
                data: mediaDate,
                dataRaw: mediaDate,
            });
        });
    });

    if (!filter?.dateRange?.startDate && !filter?.dateRange?.endDate) {
        return expandedRows;
    }

    return expandedRows.filter((row) => {
        if (!row.dataRaw) {
            return false;
        }

        return isDateInRange(row.dataRaw, filter.dateRange);
    });
};
export function processFormFields(item, dataTable, customFields = null) {
    const fields = customFields || dataTable.json.form.fields;
    fields.forEach(field => {
        if (field.dependencies && field.dependencies.inputID) {
            // Get the dependency field value from item
            const dependencyValue = item[field.dependencies.inputID];

            const dependencyMatches = Array.isArray(dependencyValue)
                ? dependencyValue.includes(field.dependencies.value)
                // eslint-disable-next-line eqeqeq
                : dependencyValue == field.dependencies.value;

            // If dependency value doesn't match, clear the dependent field
            if (!dependencyMatches) {
                item[field.inputID] = "";
            }
        }
        if (field.type === 'checkbox') {
            const checkboxValue = item[field.inputID];

            if (field.allowMultipleChoice) {
                if (Array.isArray(checkboxValue)) {
                    item[field.inputID] = checkboxValue;
                } else if (typeof checkboxValue === 'string' && checkboxValue.length > 0) {
                    try {
                        const parsed = JSON.parse(checkboxValue);
                        item[field.inputID] = Array.isArray(parsed) ? parsed : [checkboxValue];
                    } catch (e) {
                        item[field.inputID] = [checkboxValue];
                    }
                } else {
                    item[field.inputID] = [];
                }
            } else if (!field.allowMultipleChoice && field.options && field.options.length > 0) {
                if (Array.isArray(checkboxValue)) {
                    item[field.inputID] = checkboxValue[0] || "";
                } else if (checkboxValue === null || checkboxValue === undefined) {
                    item[field.inputID] = "";
                } else {
                    item[field.inputID] = String(checkboxValue);
                }
            } else {
                if (checkboxValue === null || checkboxValue === undefined) {
                    item[field.inputID] = "";
                } else {
                    item[field.inputID] = String(checkboxValue);
                }
            }
        }
    });
}

export function processData01(dataTable, data, appData, type, setFields, setFieldsResult, setShowImageModal, filter) {
    let newParticipants = JSON.parse(JSON.stringify(data.participants))
    let newHistory = JSON.parse(JSON.stringify(data.history))
    let newResults = JSON.parse(JSON.stringify(data.results))
    let newValidations = JSON.parse(JSON.stringify(data.validations))

    // Apply date range filter using data_visitacao field
    if (filter && filter.dateRange && (filter.dateRange.startDate || filter.dateRange.endDate)) {
        newParticipants = newParticipants.filter(participant => {
            if (!participant.data_visitacao) {
                return false;
            }

            return isDateInRange(participant.data_visitacao, filter.dateRange);
        });

        // Filter history to match filtered participants
        const filteredVisitanteIds = new Set(newParticipants.map(p => p.visitante_id));
        newHistory = newHistory.filter(h => filteredVisitanteIds.has(h.visitante_id));

        // Filter results based on date range
        newResults = newResults.filter(result => {
            if (!result.data) {
                return false;
            }

            return isDateInRange(result.data, filter.dateRange);
        });
    }

    // Sort results by date descending (newest first)
    newResults.sort((a, b) => {
        const dateA = getDateTimestamp(a.data) ?? 0;
        const dateB = getDateTimestamp(b.data) ?? 0;
        return dateB - dateA;
    });

    // Store total counts before processing
    const totalParticipants = newParticipants.length;
    const totalResults = newResults.length;

    switch (type) {
        case 1:
            let finalHistory = []
            newParticipants.forEach(participant => {
                participant.participantDetails = normalizeJsonObject(participant.json);
                participant.participantDetails.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(participant.data_visitacao) }
                const transformedDetails = {};
                for (const key in participant.participantDetails) {
                    const detail = participant.participantDetails[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(participant, transformedDetails);
                processFormFields(participant, dataTable);

                newHistory.forEach(historyItem => {
                    historyItem.customizacao_id = appData.customizacao_id
                    historyItem.evento_id = appData.evento_id
                    if (participant.visitante_id === historyItem.visitante_id) {
                        if (historyItem.json) {
                            Object.assign(historyItem, normalizeJsonObject(historyItem.json));
                            participant.timeX = historyItem.time
                            let convertedTime = convertMilliseconds(historyItem.time);
                            convertedTime = convertedTime['minutes'] + ':' + convertedTime['seconds'] + ':' + convertedTime['milliseconds']
                            participant.time = convertedTime
                            participant.score = historyItem.player1.value + historyItem.player2.value
                        }
                    }
                });
                finalHistory.push(participant)
            });
            finalHistory = sortRanking(finalHistory)
            setFields((prevState) => {
                const position = prevState.length - 1; // Calculate the position to insert the new object
                const newArray = [
                    ...prevState.slice(0, position), // Elements before the insertion point
                    {
                        Header: "Pontuação",
                        accessor: "score",
                        Filter: NumberRangeColumnFilter,
                        filter: "between",
                    },
                    {
                        Header: "Tempo", accessor: "time"
                    },
                    ...prevState.slice(position) // Elements after the insertion point
                ];
                return newArray;
            });
            return {
                'participants': newParticipants,
                'history': finalHistory,
                'results': newResults,
                'totalParticipants': totalParticipants,
                'totalResults': totalResults
            };

        case 2:
            //App data manipulation
            let finalHistory2 = []
            newParticipants.forEach(participant => {
                participant.participantDetails = normalizeJsonObject(participant.json);
                participant.participantDetails.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(participant.data_visitacao) }
                const transformedDetails = {};
                for (const key in participant.participantDetails) {
                    const detail = participant.participantDetails[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(participant, transformedDetails);
                processFormFields(participant, dataTable);

                newHistory.forEach(historyItem => {
                    historyItem.customizacao_id = appData.customizacao_id
                    historyItem.evento_id = appData.evento_id
                    if (participant.visitante_id === historyItem.visitante_id) {
                        if (historyItem.json) {
                            Object.assign(historyItem, normalizeJsonObject(historyItem.json));
                            participant.timeX = historyItem.time
                            let convertedTime = convertMilliseconds(historyItem.time);
                            convertedTime = convertedTime['minutes'] + ':' + convertedTime['seconds'] + ':' + convertedTime['milliseconds']
                            participant.time = convertedTime
                            participant.score = historyItem.value
                            let totalQuestions = 0
                            if (appData.json.selectQuestions && appData.json.selectQuestions.status) {
                                totalQuestions = appData.json.selectQuestions.value
                            } else {
                                totalQuestions = appData.json.questions?.length ? appData.json.questions?.length : 0
                            }
                            participant.currentIndex = historyItem.answers.length
                            participant.currentQuestion = historyItem.answers.length + '/' + totalQuestions
                            if (filter.extraPropsParticipants) {
                                historyItem.answers.forEach(answer => {
                                    if (answer === null) {
                                        return;
                                    }
                                    let selectedQuestion = appData.json.questions.find(question => question.question_id === answer.question_id);
                                    let selectedOrder = selectedQuestion ? selectedQuestion.answers.find(selectedAnswer => selectedAnswer.order === parseInt(answer.order)) : null
                                    participant['answer' + answer.question_id] = selectedOrder ? selectedOrder.text : ''
                                });
                            }
                        }
                    }
                });
                finalHistory2.push(participant)
            });
            finalHistory2 = sortRanking(finalHistory2)
            setFields((prevState) => {
                const position = prevState.length - 1; // Calculate the position to insert the new object

                const dynamicColumns = [];
                if (filter.extraPropsParticipants) {
                    appData.json.questions.forEach(item => {
                        dynamicColumns.push({
                            Header: item.question,
                            accessor: 'answer' + item.question_id,
                            Filter: SelectColumnFilter,
                            filter: "equals"
                        });
                    })
                }

                const newArray = [
                    ...prevState.slice(0, position), // Elements before the insertion point
                    {
                        Header: "Pontuação",
                        accessor: "score",
                        Filter: NumberRangeColumnFilter,
                        filter: "between",
                    },
                    {
                        Header: "Tempo", accessor: "time"
                    },
                    {
                        Header: "Questão",
                        accessor: "currentQuestion",
                        Filter: SelectColumnFilter,
                        filter: "equals"
                    },
                    ...dynamicColumns,
                    ...prevState.slice(position) // Elements after the insertion point
                ];
                return newArray;
            });
            return {
                'participants': newParticipants,
                'history': finalHistory2,
                'results': newResults,
                'totalParticipants': totalParticipants,
                'totalResults': totalResults
            };
        case 3:
            newParticipants.forEach(participant => {
                participant.participantDetails = normalizeJsonObject(participant.json);
                participant.participantDetails.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(participant.data_visitacao) }
                const transformedDetails = {};
                for (const key in participant.participantDetails) {
                    const detail = participant.participantDetails[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(participant, transformedDetails);
                processFormFields(participant, dataTable);
            });

            newHistory.forEach(historyItem => {
                historyItem.jsonUsuario = normalizeJsonObject(historyItem.jsonUsuario);
                historyItem.jsonUsuario.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(historyItem.data_visitacao) }
                const transformedDetails = {};
                for (const key in historyItem.jsonUsuario) {
                    const detail = historyItem.jsonUsuario[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(historyItem, transformedDetails);
                processFormFields(historyItem, dataTable);

                switch (historyItem.aprovado) {
                    case 1:
                        historyItem.aprovado = 'Aprovado'
                        break;
                    case 2:
                        historyItem.aprovado = 'Reprovado'
                        break;
                    default:
                        historyItem.aprovado = 'Em análise'
                        break;
                }
                historyItem.lido = historyItem.lido === 0 ? 'Não' : 'Sim'
                historyItem.favorito = historyItem.favorito === 0 ? 'Não' : 'Sim'
                historyItem.data = historyItem.data ? convertDateToDisplay(historyItem.data) : ''
            });

            setFields((prevState) => {
                const position = prevState.length - 1; // Calculate the position to insert the new object

                // Create the new array by concatenating slices and new objects
                const newArray = [
                    ...prevState.slice(0, position),
                    {
                        Header: "Pergunta",
                        accessor: "message",
                        Cell: (cell) => {
                            return (
                                <div style={{ minWidth: "250px" }}>{cell?.row?.original.message}</div>
                            )
                        }
                    },
                    {
                        Header: "Aprovado",
                        accessor: "aprovado",
                        Filter: SelectColumnFilter,
                        filter: "equals",
                    },
                    {
                        Header: "Lido",
                        accessor: "lido",
                        Filter: SelectColumnFilter,
                        filter: "equals",
                    },
                    {
                        Header: "Data de leitura",
                        accessor: "data"
                    },
                    {
                        Header: "Favorito",
                        accessor: "favorito",
                        Filter: SelectColumnFilter,
                        filter: "equals",
                    },
                    ...prevState.slice(position) // Elements after the insertion point
                ];
                return newArray;
            });
            return {
                'participants': newParticipants,
                'history': newHistory,
                'results': newResults,
                'totalParticipants': totalParticipants,
                'totalResults': totalResults
            };
        case 4:
            newParticipants.forEach(participant => {
                participant.participantDetails = normalizeJsonObject(participant.json);
                participant.participantDetails.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(participant.data_visitacao) }
                const transformedDetails = {};
                for (const key in participant.participantDetails) {
                    const detail = participant.participantDetails[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(participant, transformedDetails);
                processFormFields(participant, dataTable);
            });

            newHistory.forEach(historyItem => {
                historyItem.jsonUsuario = normalizeJsonObject(historyItem.jsonUsuario);
                historyItem.jsonUsuario.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(historyItem.data_visitacao) }
                const transformedDetails = {};
                for (const key in historyItem.jsonUsuario) {
                    const detail = historyItem.jsonUsuario[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(historyItem, transformedDetails);
                processFormFields(historyItem, dataTable);

                switch (historyItem.aprovado) {
                    case 1:
                        historyItem.aprovado = 'Aprovado'
                        break;
                    case 2:
                        historyItem.aprovado = 'Reprovado'
                        break;
                    default:
                        historyItem.aprovado = 'Em análise'
                        break;
                }
                historyItem.lido = historyItem.lido === 0 ? 'Não' : 'Sim'
                historyItem.favorito = historyItem.favorito === 0 ? 'Não' : 'Sim'
                historyItem.data = historyItem.data ? convertDateToDisplay(historyItem.data) : ''
                if (historyItem.json) {
                    historyItem.json = normalizeJsonObject(historyItem.json)
                    if (historyItem.json.recipient !== undefined && historyItem.json.recipient !== '') {
                        historyItem.recipient = historyItem.json.recipient
                    }
                }
            });
            setFields((prevState) => {
                const position = prevState.length - 1; // Calculate the position to insert the new object
                // Create the new array by concatenating slices and new objects
                const newArray = [
                    ...prevState.slice(0, position),
                    {
                        Header: "Destinatário",
                        accessor: "recipient"
                    },
                    {
                        Header: "Mensagem",
                        accessor: "message",
                        Cell: (cell) => {
                            return (
                                <div style={{ minWidth: "250px" }}>{cell?.row?.original.message}</div>
                            )
                        }
                    },
                    {
                        Header: "Aprovado",
                        accessor: "aprovado",
                        Filter: SelectColumnFilter,
                        filter: "equals",
                    },
                    {
                        Header: "Lido",
                        accessor: "lido",
                        Filter: SelectColumnFilter,
                        filter: "equals",
                    },
                    {
                        Header: "Data de leitura",
                        accessor: "data"
                    },
                    {
                        Header: "Favorito",
                        accessor: "favorito",
                        Filter: SelectColumnFilter,
                        filter: "equals",
                    },
                    ...prevState.slice(position) // Elements after the insertion point
                ];
                return newArray;
            });
            return {
                'participants': newParticipants,
                'history': newHistory,
                'results': newResults,
                'totalParticipants': totalParticipants,
                'totalResults': totalResults
            };
        case 5:
            newParticipants.forEach(participant => {
                participant.participantDetails = normalizeJsonObject(participant.json);
                participant.participantDetails.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(participant.data_visitacao) }
                const transformedDetails = {};
                for (const key in participant.participantDetails) {
                    const detail = participant.participantDetails[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(participant, transformedDetails);

                processFormFields(participant, dataTable);
            });

            newHistory.forEach(historyItem => {
                historyItem.jsonUsuario = normalizeJsonObject(historyItem.jsonUsuario);
                historyItem.jsonUsuario.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(historyItem.data_visitacao) }
                const transformedDetails = {};
                for (const key in historyItem.jsonUsuario) {
                    const detail = historyItem.jsonUsuario[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(historyItem, transformedDetails);

                processFormFields(historyItem, dataTable);

                switch (historyItem.aprovado) {
                    case 1:
                        historyItem.aprovado = 'Aprovado'
                        break;
                    case 2:
                        historyItem.aprovado = 'Reprovado'
                        break;
                    default:
                        historyItem.aprovado = 'Em análise'
                        break;
                }
                let words = JSON.parse(historyItem.message);
                historyItem.message = words.join(", ")
                historyItem.lido = historyItem.lido === 0 ? 'Não' : 'Sim'
                historyItem.favorito = historyItem.favorito === 0 ? 'Não' : 'Sim'
                historyItem.data = historyItem.data ? convertDateToDisplay(historyItem.data) : ''
            });
            setFields((prevState) => {
                const position = prevState.length - 1; // Calculate the position to insert the new object
                // Create the new array by concatenating slices and new objects
                const newArray = [
                    ...prevState.slice(0, position),
                    {
                        Header: "Conjunto de palavras",
                        accessor: "message",
                        Cell: (cell) => {
                            return (
                                <div style={{ minWidth: "250px" }}>{cell?.row?.original.message}</div>
                            )
                        }
                    },
                    {
                        Header: "Aprovado",
                        accessor: "aprovado",
                        Filter: SelectColumnFilter,
                        filter: "equals",
                    },
                    {
                        Header: "Lido",
                        accessor: "lido",
                        Filter: SelectColumnFilter,
                        filter: "equals",
                    },
                    {
                        Header: "Data de leitura",
                        accessor: "data"
                    },
                    {
                        Header: "Favorito",
                        accessor: "favorito",
                        Filter: SelectColumnFilter,
                        filter: "equals",
                    },
                    ...prevState.slice(position) // Elements after the insertion point
                ];
                return newArray;
            });
            return {
                'participants': newParticipants,
                'history': newHistory,
                'results': newResults,
                'totalParticipants': totalParticipants,
                'totalResults': totalResults
            };
        case 6:
            const indexedData = {};
            let labelX = null
            let finalHistory6 = []
            dataTable.json.form.fieldsToShow.forEach(inputID => {
                const field = dataTable.json.form.fields.find(field => field.inputID === inputID);
                if (field && labelX === null) {
                    labelX = field[inputID]
                }
            });
            newParticipants.forEach(participant => {
                participant.history = []
                participant.participantDetails = normalizeJsonObject(participant.json);
                participant.participantDetails.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(participant.data_visitacao) }
                const transformedDetails = {};
                for (const key in participant.participantDetails) {
                    const detail = participant.participantDetails[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(participant, transformedDetails);

                processFormFields(participant, dataTable);

                participant.idDraw = []
                participant.prize = []
                participant.validadoString = 'Não'
                indexedData[participant.visitante_id] = participant; // Use the value of 'id' as the key
                newHistory.forEach(historyItem => {
                    historyItem.customizacao_id = appData.customizacao_id
                    historyItem.evento_id = appData.evento_id
                    if (participant.visitante_id === historyItem.visitante_id) {
                        Object.assign(historyItem, transformedDetails);
                        if (historyItem.json) {
                            let item = normalizeJsonObject(historyItem.json)
                            let index = 0
                            item.forEach(element => {
                                index++
                                participant['Premio0' + index] = element.prize
                            });
                        }
                    }
                });
                finalHistory6.push(participant)
            });
            finalHistory6 = sortMessages(finalHistory6)

            let finalResultList = []
            let participantsDrawsColumns = []
            //adicionar id de identificaçao??
            Object.values(newResults).forEach(historyItem => {
                const persistedData = historyItem.data;
                Object.assign(historyItem, normalizeJsonObject(historyItem.json));
                historyItem.data = convertDateToDisplay(persistedData);
                historyItem.typeString = returnDrawType(historyItem.type)
                historyItem.customizacao_id = appData.customizacao_id
                historyItem.evento_id = appData.evento_id
                historyItem.result.forEach(element => {
                    let addedDraw = participantsDrawsColumns.find(item => item.accessor === historyItem.id);
                    if (!addedDraw) {
                        participantsDrawsColumns.push({
                            Header: historyItem.title,
                            accessor: historyItem.id
                        });
                    }
                    if (historyItem.type === 'DrawDefault') {
                        if (indexedData.hasOwnProperty(element.visitante_id)) {
                            if (historyItem.prize !== '' && historyItem.prize !== null) {
                                indexedData[element.visitante_id][historyItem.id] = historyItem.prize
                            }
                            const validado = newValidations.find(item => item.visitante_id === element.visitante_id);
                            const validadoString = validado ? validado.validado === 1 ? 'Sim' : 'Não' : 'Não';
                            indexedData[element.visitante_id]['validadoString'] = validadoString
                        }
                        finalResultList.push({
                            "customizacao_id": appData.customizacao_id,
                            "evento_id": appData.evento_id,
                            "resultado_id": historyItem.resultado_id,
                            "id": historyItem.id,
                            "title": historyItem.title,
                            "typeString": historyItem.typeString,
                            "prize": historyItem.prize,
                            "result": indexedData[element.visitante_id] && dataTable.json.form.fieldsToShow
                                ? dataTable.json.form.fieldsToShow
                                    .map(inputID => indexedData[element.visitante_id][inputID])
                                : [],
                            "data": historyItem.data
                        })
                    } else {
                        finalResultList.push({
                            "customizacao_id": appData.customizacao_id,
                            "evento_id": appData.evento_id,
                            "resultado_id": historyItem.resultado_id,
                            "id": historyItem.id,
                            "title": historyItem.title,
                            "typeString": historyItem.typeString,
                            "prize": historyItem.prize,
                            "result": element,
                            "data": historyItem.data
                        })
                    }
                })
            });
            setFields((prevState) => {
                let position = prevState.length - 1; // Calculate the position to insert the new object
                let newArray = []
                let validate = []
                if (appData.json.prizeDrawType === 2) {
                    validate.push({
                        Header: "Validado",
                        accessor: "validadoString",
                        Filter: SelectColumnFilter,
                        filter: "equals",
                    })
                }
                if (appData.json.drawMode === 2) {
                    const newObjects = [];
                    for (let index = 1; index <= appData.json.maxDraws; index++) {
                        newObjects.push({
                            Header: `Premio0${index}`,
                            accessor: `Premio0${index}`,
                            Cell: (cell) => {
                                return cell?.row?.original['Premio0' + index] || null
                            }
                        });
                    }

                    newArray = [
                        ...prevState.slice(0, position), // Elements before the insertion point
                        ...validate,
                        ...newObjects,
                        ...prevState.slice(position) // Elements after the insertion point
                    ];
                    return newArray;
                } else {
                    newArray = [
                        ...prevState.slice(0, position), // Elements before the insertion point
                        ...validate,
                        ...participantsDrawsColumns,
                        ...prevState.slice(position) // Elements after the insertion point
                    ];
                }
                return newArray;
            });
            setFieldsResult((prevState) => {
                const position = prevState.length - 1; // Calculate the position to insert the new object
                const newArray = [
                    {
                        Header: 'ID',
                        accessor: "id"
                    },
                    {
                        Header: "Título",
                        accessor: "title"
                    }, {
                        Header: "Tipo de sorteio",
                        accessor: "typeString",
                        Filter: SelectColumnFilter,
                        filter: "equals",
                    },
                    {
                        Header: "Premiação",
                        accessor: "prize",
                        Cell: ({ value }) => (
                            <div className="text-center">
                                {Array.isArray(value)
                                    ? value.map((v, i) => <div key={i}>{v}</div>)
                                    : value}
                            </div>
                        )
                    },
                    {
                        Header: "Ganhador",
                        accessor: "result",
                        Cell: ({ value }) => (
                            <div className="text-center">
                                {Array.isArray(value)
                                    ? value.map((v, i) => <div key={i}>{v}</div>)
                                    : value}
                            </div>
                        )
                    },
                    {
                        Header: "Data",
                        accessor: "data"
                    },
                    ...prevState.slice(position) // Elements after the insertion point
                ];
                return newArray;
            });

            return {
                'participants': newParticipants,
                'history': finalHistory6,
                'results': finalResultList,
                'totalParticipants': totalParticipants,
                'totalResults': totalResults
            };
        case 7:
            let finalHistory7 = []
            newParticipants.forEach(participant => {
                participant.participantDetails = normalizeJsonObject(participant.json);
                participant.participantDetails.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(participant.data_visitacao) }
                const transformedDetails = {};
                for (const key in participant.participantDetails) {
                    const detail = participant.participantDetails[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(participant, transformedDetails);

                processFormFields(participant, dataTable);

                newHistory.forEach(historyItem => {
                    historyItem.customizacao_id = appData.customizacao_id
                    historyItem.evento_id = appData.evento_id
                    if (participant.visitante_id === historyItem.visitante_id) {
                        if (historyItem.json) {
                            const persistedData = historyItem.data;
                            Object.assign(historyItem, normalizeJsonObject(historyItem.json));
                            historyItem.data = persistedData;
                            participant.label = historyItem.label
                            participant.prize = historyItem.prize
                        }
                    }
                });
                finalHistory7.push(participant)
            });
            finalHistory7 = sortRanking(finalHistory7)
            setFields((prevState) => {
                const position = prevState.length - 1; // Calculate the position to insert the new object
                const newArray = [
                    ...prevState.slice(0, position), // Elements before the insertion point
                    {
                        Header: "Segmento",
                        accessor: "label",
                        Filter: SelectColumnFilter,
                        filter: "equals",
                    },
                    {
                        Header: "Premiação",
                        accessor: "prize"
                    },
                    ...prevState.slice(position) // Elements after the insertion point
                ];
                return newArray;
            });

            //results
            newResults.forEach(resultItem => {
                resultItem.customizacao_id = appData.customizacao_id
                resultItem.evento_id = appData.evento_id
                const persistedData = resultItem.data;
                if (resultItem.json) {
                    Object.assign(resultItem, normalizeJsonObject(resultItem.json));
                }
                resultItem.data = convertDateToDisplay(persistedData);
            });
            setFieldsResult([
                {
                    Header: 'ID',
                    accessor: "id"
                },
                {
                    Header: "Segmento",
                    accessor: "label",
                    Filter: SelectColumnFilter,
                    filter: "equals",
                },
                {
                    Header: "Premiação",
                    accessor: "prize"
                },
                {
                    Header: "Data",
                    accessor: "data"
                },
            ])
            return {
                'participants': newParticipants,
                'history': finalHistory7,
                'results': newResults,
                'totalParticipants': totalParticipants,
                'totalResults': totalResults
            };

        case 10:
            // Utility to check media type by file extension
            const checkMediaType = (src) => {
                if (!src || typeof src !== "string") return "unknown";
                const ext = src.split('.').pop().toLowerCase();
                const videoExts = ["mp4", "webm", "mov", "avi", "mkv", "flv", "wmv", "m4v"];
                if (videoExts.includes(ext)) return "video";
                const imageExts = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "tiff", "ico"];
                if (imageExts.includes(ext)) return "image";
                return "unknown";
            };
            let finalHistory10 = []
            newHistory = newHistory.map(historyItem => {
                let parsedItem = normalizeJsonObject(historyItem.json);
                parsedItem.data = historyItem.data;
                parsedItem.visitante_id = historyItem.visitante_id;
                return parsedItem;
            })

            newParticipants.forEach(participant => {
                participant.participantDetails = normalizeJsonObject(participant.json);
                participant.participantDetails.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(participant.data_visitacao) }
                const transformedDetails = {};
                for (const key in participant.participantDetails) {
                    const detail = participant.participantDetails[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(participant, transformedDetails);

                processFormFields(participant, dataTable);

                newHistory.forEach(historyItem => {
                    historyItem.customizacao_id = appData.customizacao_id
                    historyItem.evento_id = appData.evento_id
                    if (participant.visitante_id === historyItem.visitante_id) {
                        participant.media = historyItem.media
                        participant.date = historyItem.data ? convertDateToDisplay(historyItem.data) : ''
                    }
                });
                finalHistory10.push(participant)
            });
            finalHistory10 = sortDate(finalHistory10)
            setFields((prevState) => {
                const position = prevState.length - 1; // Calculate the position to insert the new object
                const newArray = [
                    ...prevState.slice(0, position), // Elements before the insertion point
                    {
                        Header: "Data",
                        accessor: "date"
                    },
                    {
                        Header: "Mídia", accessor: "media", Cell: (cell) => {
                            if (cell.row.original.media) {
                                const mediaUrl = cell.row.original.media;
                                const isVideoUrl = (src) => {
                                    if (!src || typeof src !== "string") return false;
                                    const cleanSrc = src.split("?")[0].split("#")[0];
                                    return checkMediaType(cleanSrc) === "video";
                                };
                                const label = isVideoUrl(mediaUrl) ? "Vídeo" : "Foto";
                                return (
                                    <a onClick={(e) => { e.preventDefault(); setShowImageModal({ url: mediaUrl, show: true }) }} href={mediaUrl} rel="noreferrer">{label}</a>
                                )
                            } else {
                                return ""
                            }
                        },
                    },
                    ...prevState.slice(position) // Elements after the insertion point
                ];
                return newArray;
            });
            return {
                'participants': newParticipants,
                'history': finalHistory10,
                'results': newResults,
                'totalParticipants': totalParticipants,
                'totalResults': totalResults
            };
        case 11:
            const app11MediaRows = expandApp11MediaRows(newHistory, appData, filter);
            const app11MediaRowsByVisitor = new Map();

            app11MediaRows.forEach((historyItem) => {
                const visitorMediaRows = app11MediaRowsByVisitor.get(historyItem.visitante_id) || [];
                visitorMediaRows.push(historyItem);
                app11MediaRowsByVisitor.set(historyItem.visitante_id, visitorMediaRows);
            });

            let finalHistory11 = []
            newParticipants.forEach(participant => {
                participant.participantDetails = normalizeJsonObject(participant.json);
                participant.participantDetails.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(participant.data_visitacao) }
                const transformedDetails = {};
                for (const key in participant.participantDetails) {
                    const detail = participant.participantDetails[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(participant, transformedDetails);

                processFormFields(participant, dataTable);

                const participantMediaRows = app11MediaRowsByVisitor.get(participant.visitante_id) || [];
                participant.mediaItems = participantMediaRows;
                participant.mediaTotal = participantMediaRows.length;
                participant.media = participantMediaRows.map((item) => item.media);
                participant.customizacao_id = appData.customizacao_id;
                participant.evento_id = appData.evento_id;

                finalHistory11.push(participant)
            });
            finalHistory11 = sortDate(finalHistory11)

            setFields((prevState) => {
                const position = prevState.length - 1;
                const newArray = [
                    ...prevState.slice(0, position),
                    {
                        Header: "Mídia",
                        accessor: "mediaItems",
                        Cell: (cell) => {
                            const mediaItems = cell.row.original.mediaItems || [];

                            if (mediaItems.length === 0) {
                                return "";
                            }

                            return (
                                <>
                                    {mediaItems.map((mediaItem, index) => (
                                        <a
                                            key={mediaItem.mediaKey || `${cell.row.original.visitante_id}-${index}`}
                                            className="me-2"
                                            onClick={(e) => { e.preventDefault(); setShowImageModal({ url: mediaItem.media, show: true }) }}
                                            href={mediaItem.media}
                                            rel="noreferrer"
                                        >
                                            {mediaItem.mediaPosition || String(index + 1).padStart(2, '0')}
                                        </a>
                                    ))}
                                </>
                            );
                        },
                    },
                    ...prevState.slice(position)
                ];
                return newArray;
            });
            return {
                'participants': newParticipants,
                'history': finalHistory11,
                'results': [],
                'totalParticipants': totalParticipants,
                'totalResults': 0
            };
        default:
            newParticipants.forEach(participant => {
                participant.participantDetails = normalizeJsonObject(participant.json);
                participant.participantDetails.data_visitacao_formatted = { 'label': 'data_visitacao_formatted', 'value': convertDateToDisplay(participant.data_visitacao) }
                const transformedDetails = {};
                for (const key in participant.participantDetails) {
                    const detail = participant.participantDetails[key];
                    transformedDetails[key] = getJsonFieldValue(detail);
                }

                // Assign transformed details back to participant
                Object.assign(participant, transformedDetails);

                processFormFields(participant, dataTable);
            });
            return {
                'participants': newParticipants,
                'history': newParticipants,
                'results': [],
                'totalParticipants': totalParticipants,
                'totalResults': totalResults
            };
    }
}

export function generateAccessData01(entries) {
    if (!entries) {
        return ""
    }
    const today = new Date();
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const daysInMonth = endOfMonth.getDate();
    const dataCounts = Array(daysInMonth).fill(0);

    // Generate dates for 7 days before and after today
    const dateRange = [];
    for (let i = 1; i <= daysInMonth; i++) {
        const newDate = new Date(today.getFullYear(), today.getMonth(), i);
        dateRange.push(newDate);
    }

    // Count the number of accesses for each day in the ranges
    entries.forEach(entry => {
        for (let i = 0; i < dateRange.length; i++) {
            let newDateEntry
            if (entry.data_visitacao) {
                newDateEntry = parseFlexibleDate(entry.data_visitacao);
            } else {
                newDateEntry = parseFlexibleDate(entry.creation_date);
            }
            if (newDateEntry.toDateString() === dateRange[i].toDateString()) {
                dataCounts[i]++;
                break;
            }
        }
    });
    const series = [
        {
            name: 'Total de registros',
            data: dataCounts
        }
    ];
    return series
}

export function setHeaders01(dataTable, handleDelete, filter, handleEdit, canEdit) {
    const newFields = [];
    if (dataTable.json.form.fields.length === 0) {
        newFields.push({ Header: "ID", accessor: "visitante_id" });

    } else {
        if (filter.extraPropsParticipants) {
            newFields.push({ Header: "ID", accessor: "visitante_id" });
        }

        let checkShowInUserReport = []
        dataTable.json.form.fields.forEach((element) => {
            if (element.showInUserReport) {
                checkShowInUserReport.push(element)
            }
        })

        dataTable.json.form.fields.forEach((element) => {
            if (!element.showInUserReport && !filter.extraPropsParticipants && checkShowInUserReport.length > 0) {
                return;
            }
            let maxLength = 40;
            let truncatedString = element.label.length > maxLength
                ? element.label.slice(0, maxLength - 3) + "..."
                : element.label;

            switch (element.type) {
                case 'textarea':
                case 'input':
                    if (element.validation === 'number') {
                        newFields.push({
                            Header: truncatedString,
                            accessor: String(element.inputID),
                            Filter: NumberRangeColumnFilter,
                            filter: "between",
                        });
                    } else {
                        newFields.push({
                            Header: truncatedString,
                            accessor: String(element.inputID),
                        });
                    }
                    break;
                case 'checkbox':
                case 'select':
                    newFields.push({
                        Header: truncatedString,
                        accessor: String(element.inputID),
                        Filter: SelectColumnFilter,
                        filter: "equals",
                        Cell: ({ value }) => {
                            if (Array.isArray(value) && value.length > 0) {
                                return (
                                    <div>
                                        {value.map((item, index) => (
                                            <div key={index} style={{ marginBottom: '2px', display: 'flex', alignItems: 'flex-start' }}>
                                                <span style={{ marginRight: '6px', fontWeight: 'bold' }}>&bull;</span>
                                                <span>{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                );
                            } else {
                                return value ?? null;
                            }
                        }
                    });
                    break;
                default:
                    newFields.push({
                        Header: truncatedString,
                        accessor: String(element.inputID),
                    });
                    break;
            }
        });
        newFields.push({ Header: "Data de registro", accessor: "data_visitacao_formatted" });
    }
    if (filter.extraPropsParticipants) {
        newFields.push({
            Header: "Origem",
            accessor: "origin",
            Filter: SelectColumnFilter,
            filter: "equals",
            renderFilterLabel: getOriginLabel,
            Cell: ({ value }) => value ? getOriginLabel(value) : "—",
            exportValue: (_, value) => getOriginLabel(value) || "—",
        });
    }
    newFields.push({
        Header: "",
        accessor: 'icons',
        disableSortBy: true,
        Filter: false,
        Cell: (cell) => {
            let props = { platform: 1, id: cell.row.original.visitante_id, evento_id: cell.row.original.evento_id, customizacao_id: cell.row.original.customizacao_id, type: 'removeUsers' }
            return (
                <>
                    {canEdit && (
                    <OverlayTrigger overlay={<Tooltip>Excluir participante</Tooltip>}>
                        <Trash
                            role="button"
                            className="align-middle cursor-pointer me-2"
                            onClick={() => handleDelete(props, "Deseja remover esse participante? Todos os dados serão excluídos.")}
                            size={18}
                        />
                    </OverlayTrigger>
                    )}
                    <OverlayTrigger overlay={<Tooltip>Baixar Código QR</Tooltip>}>
                        <Download
                            role="button"
                            className="align-middle cursor-pointer me-2"
                            onClick={() => {
                                let code = createQRCodePlatform01(cell.row.original)
                                downloadQRCode(code, cell.row.original.visitante_id);
                            }}
                            size={18}
                        />
                    </OverlayTrigger>
                    {canEdit && (
                    <OverlayTrigger overlay={<Tooltip>Editar participante</Tooltip>}>
                        <Edit
                            role="button"
                            className="align-middle cursor-pointer"
                            onClick={() => handleEdit(cell.row.original)}
                            size={18}
                        />
                    </OverlayTrigger>
                    )}
                </>
            );
        },
    });

    return newFields
    // eslint-disable-next-line react-hooks/exhaustive-deps
}

export function setHeadersResult(setFieldsResult, handleDelete, canEdit) {

    const newFields = [];
    newFields.push({
        Header: "",
        accessor: 'icons',
        disableSortBy: true,
        Filter: false,
        Cell: (cell) => {
            let props = { platform: 1, id: cell.row.original.resultado_id, visitante_id: cell.row.original.visitante_id, mediaIndex: cell.row.original.mediaIndex, mediaUrl: cell.row.original.media, evento_id: cell.row.original.evento_id, customizacao_id: cell.row.original.customizacao_id, type: 'removeUsersResults' }
            return (
                <>
                    {canEdit && (
                    <OverlayTrigger overlay={<Tooltip>Excluir resultado</Tooltip>}>
                        <Trash
                            role="button"
                            className="align-middle cursor-pointer"
                            onClick={() => handleDelete(props, "Ao continuar, todos os resultados relacionados a esse ID serão excluídos?")}
                            size={18}
                        />
                    </OverlayTrigger>
                    )}
                </>
            );
        },
    });
    setFieldsResult(newFields);
}

export function filterHistoryQuiz(participants) {
    participants.forEach(item => {
        const parts = item.time.split(':');
        const minutes = parseInt(parts[0], 10);
        const seconds = parseInt(parts[1], 10);
        const milliseconds = parseInt(parts[2], 10);
        item.timeX = (minutes * 60 * 1000) + (seconds * 1000) + milliseconds;
    });
    participants.sort((a, b) => {
        const scoreA = a.value || 0;
        const scoreB = b.value || 0;
        const timeA = a.timeX || 0; // Set default to Infinity instead of 0
        const timeB = b.timeX || 0; // Set default to Infinity instead of 0
        if (a.currentIndex !== b.currentIndex) {
            return b.currentIndex - a.currentIndex;
        }
        if (scoreA !== scoreB) {
            return scoreB - scoreA;
        }
        if (timeA !== timeB) {
            return timeA - timeB;
        }
        return 0
    });

    // Assign ranking after sorting
    participants.forEach((participant, index) => {
        participant.ranking = index + 1;
    });

    return participants
}

export const getQuizResult = (answers, appData) => {
    let result = []
    answers.forEach(element => {
        appData.json.questions.forEach(ans => {
            if (element.question_id === ans.question_id) {
                ans.answers.forEach(value => {
                    if (element.order === value.order) {
                        if (!result[value.resultId]) {
                            result[value.resultId] = 0
                        }
                    }
                });
            }
        });
    });

    const resultId = result.reduce((maxIndex, currentValue, currentIndex, array) => {
        if (currentValue !== null && (maxIndex === -1 || currentValue > array[maxIndex])) {
            return currentIndex;
        }
        return maxIndex;
    }, -1);

    const finalResult = appData.json.result.find(r => r.resultId === resultId);
    return finalResult
}

export const sortRanking = (data) => {
    data.sort((a, b) => {
        const currentIndexA = a.currentIndex || 0;
        const currentIndexB = b.currentIndex || 0;
        const scoreA = a.score || 0;
        const scoreB = b.score || 0;
        const timeA = a.timeX || Infinity; // Set default to Infinity instead of 0
        const timeB = b.timeX || Infinity; // Set default to Infinity instead of 0
        if (currentIndexA !== currentIndexB) {
            return currentIndexB - currentIndexA;
        }

        if (scoreA !== scoreB) {
            return scoreB - scoreA;
        }
        if (timeA !== timeB) {
            return timeA - timeB;
        }
        return 0
    });
    return data
}

export const sortMessages = (data) => {
    data.sort((a, b) => {
        const dateA = a.data_visitacao ? getDateTimestamp(a.data_visitacao) : getDateTimestamp(a.data);
        const dateB = b.data_visitacao ? getDateTimestamp(b.data_visitacao) : getDateTimestamp(b.data);
        if (a.favorito > b.favorito) return -1;
        if (a.favorito < b.favorito) return 1;
        if (a.lido < b.lido) return -1;
        if (a.lido > b.lido) return 1;
        if ((dateA ?? 0) > (dateB ?? 0)) return -1; // Ascending order
        if ((dateA ?? 0) < (dateB ?? 0)) return 1;
        return 0;
    });
    return data
}

export const sortDate = (data) => {
    data.sort((a, b) => {
        const dateA = a.data_visitacao ? getDateTimestamp(a.data_visitacao) : getDateTimestamp(a.data);
        const dateB = b.data_visitacao ? getDateTimestamp(b.data_visitacao) : getDateTimestamp(b.data);
        if ((dateA ?? 0) > (dateB ?? 0)) return -1; // Ascending order
        if ((dateA ?? 0) < (dateB ?? 0)) return 1;
        return 0;
    });
    return data;
};
